import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { LibraryItem } from '@/data/mock'
import type { ChatResponse } from '@/types/api'
import type { AuditEntry } from '@/components/evidence/AuditTrail'
import { recalculateStatus, generateAnalysisUpdate, statusBadgeConfig, type ClaimSourceEntry, type VerificationStatus } from '@/components/evidence/statusConfig'
import { flushAuditTrail, beaconFlushAuditTrail } from '@/lib/flushAuditTrail'
import { persistReviewState } from '@/lib/persistReviewState'
import { apiFetch } from '@/lib/apiFetch'
import { citationLabel } from '@/lib/citationLabel'
import type { CitationSnapshot } from '@/types/citation'

// ── ClaimSnapshot — tracks original (immutable) vs effective (mutable) state ─

export interface ClaimSnapshot {
  claimId: string
  claimUuid?: string               // DB UUID for PATCH persistence
  originalStatus: string           // From API, never mutated (may be 'verified_no_coords')
  effectiveStatus: string          // Current status after source removal
  originalAnalysis: string         // reasoning from API, never mutated
  effectiveAnalysis: string        // Updated on source changes
  suggestedFix: string | null      // From API suggested_edit
  suggestedFixCleared: boolean     // true when status diverges from original (fix may reference removed evidence)
  sources: ClaimSourceEntry[]      // All sources for this claim (from evidences)
}

// ── Context value ────────────────────────────────────────────────────────────

export interface ReviewPanelContextValue {
  // Audit data (from App.tsx)
  apiAuditResult: (ChatResponse & { query: string }) | null
  sourceLookup: Map<string, LibraryItem>
  isAuditing: boolean
  hasAuditResults: boolean
  progressMessage: string | null
  auditError: string | null
  activeDocumentId: string | null
  isStale: boolean

  // Selection state (bidirectional with Workbench via App.tsx)
  selectedClaimId: string | null
  setSelectedClaimId: (id: string | null) => void
  focusedClaimIndices: number[]
  setFocusedClaimIndices: (indices: number[]) => void
  activeSourceId: string | null
  setActiveSourceId: (id: string | null) => void
  activeBreakdownIndices: Set<number> | null

  // Action handlers (from App.tsx)
  onRunAudit: () => void
  onRunIncrementalAudit?: () => void
  onHighlightClause: (clauseId: string | null) => void
  onSourceReject: (claimId: string, sourceId: string, page: number) => void
  onReprocessMetadata: (sourceId: string) => void
  onUpdateCitation: (sourceId: string, changes: { amaOverride?: string | null; shortOverride?: string | null }) => void
  onImportToLibrary: (item: LibraryItem) => void

  // Editor integration
  applyEdit: (originalText: string, newText: string) => boolean

  // Phase 2b state (owned by provider)
  viewMode: 'triage' | 'surgery' | 'abbreviations'
  setViewMode: (mode: 'triage' | 'surgery' | 'abbreviations') => void
  surgeryClaimId: string | null
  setSurgeryClaimId: (id: string | null) => void
  hiddenSources: Map<string, Set<string>>
  hideSource: (claimId: string, sourceId: string) => void
  unhideSource: (claimId: string, sourceId: string) => void
  /** Per-passage soft-removals, keyed by claimId. Source-level removal hides
   *  the whole source's passages implicitly via SurgeryPanel filtering; this
   *  map is the finer-grained scalpel. Persisted alongside hidden_source_ids
   *  in `claims.review_state`. */
  hiddenPassages: Map<string, Set<string>>
  removePassageFromClaim: (claimId: string, sourceId: string, passageId: string) => void
  restorePassageToClaim: (claimId: string, sourceId: string, passageId: string) => void

  // Abbreviations panel — read from current audit, edit via PATCH endpoint.
  abbreviations: { abbr: string; full: string; user_edited?: boolean }[]
  onAbbreviationsChange: (
    next: { abbr: string; full: string; user_edited?: boolean }[],
  ) => void
  draftId: string | null

  // Claim snapshots — source-removal badge recalculation (owned by provider)
  claimSnapshots: Map<string, ClaimSnapshot>
  removeSourceFromClaim: (claimId: string, sourceId: string) => void
  restoreSourceToClaim: (claimId: string, sourceId: string) => void
  /** Remove the citation node from the editor doc for this claim (D4). The
   *  claim row in DB is preserved. Adds an audit entry referencing the source
   *  whose removal triggered the conversion. Returns true if the doc was
   *  successfully mutated. */
  removeCitation: (claimId: string, triggerSourceId: string) => boolean
  /** Session-scoped record of citations the writer has removed (D11). Keyed
   *  by claim_uuid (= snapshot.claimUuid). Used by RemovedCitationsSection. */
  removedCitations: Map<string, CitationSnapshot>
  /** Re-insert a previously removed citation back into the doc and clear it
   *  from the removedCitations map. */
  restoreCitation: (claimUuid: string) => boolean

  // Audit trail (owned by provider)
  auditLog: Map<string, AuditEntry[]>
  addAuditEntry: (claimId: string, action: string, type: 'user' | 'auto') => void
}

// ── Provider props (values passed in from App.tsx) ───────────────────────────

interface ReviewPanelProviderProps {
  children: ReactNode

  // Surgery state (lifted to App.tsx)
  viewMode: 'triage' | 'surgery' | 'abbreviations'
  setViewMode: (mode: 'triage' | 'surgery' | 'abbreviations') => void
  surgeryClaimId: string | null
  setSurgeryClaimId: (id: string | null) => void

  // Audit data
  apiAuditResult: (ChatResponse & { query: string }) | null
  sourceLookup: Map<string, LibraryItem>
  isAuditing: boolean
  hasAuditResults: boolean
  progressMessage: string | null
  auditError: string | null
  activeDocumentId: string | null

  // Abbreviations panel inputs (lifted from App.tsx so PATCH can update both
  // the panel's editing state and the editor footer in one shot).
  abbreviations: { abbr: string; full: string; user_edited?: boolean }[]
  onAbbreviationsChange: (
    next: { abbr: string; full: string; user_edited?: boolean }[],
  ) => void
  draftId: string | null

  // Selection state (setters are App.tsx's own setters — bidirectional sync)
  selectedClaimId: string | null
  setSelectedClaimId: (id: string | null) => void
  focusedClaimIndices: number[]
  setFocusedClaimIndices: (indices: number[]) => void
  activeSourceId: string | null
  setActiveSourceId: (id: string | null) => void
  activeBreakdownIndices: Set<number> | null

  // Action handlers
  onRunAudit: () => void
  onRunIncrementalAudit?: () => void
  onHighlightClause: (clauseId: string | null) => void
  onSourceReject: (claimId: string, sourceId: string, page: number) => void
  onReprocessMetadata: (sourceId: string) => void
  onUpdateCitation: (sourceId: string, changes: { amaOverride?: string | null; shortOverride?: string | null }) => void
  onImportToLibrary: (item: LibraryItem) => void
  applyEdit: (originalText: string, newText: string) => boolean

  // Decoration sync callback — lifts effective statuses to App.tsx for Workbench
  onClaimStatusChange?: (map: Map<string, string>) => void
  // Source-registry sync callback — lifts hidden sources keyed by claim_uuid
  // (matches citation node `claimId` attr) to App.tsx → Workbench → registry
  // plugin via meta dispatch. Drives reactive citation labels + bibliography.
  onHiddenSourcesChange?: (map: Map<string, Set<string>>) => void
  // Doc-mutation callback for D4 — App.tsx implements via Workbench helpers
  // (which dispatch a transaction with auditProtection bypass meta). Returns
  // the captured snapshot for the restore stack, or null if no node matched.
  onRemoveCitationFromDoc?: (claimUuid: string) => CitationSnapshot | null
  /** Doc-mutation callback for D11 — re-insert a previously removed citation
   *  at its original (or clamped) position. Returns true on success. */
  onRestoreCitationToDoc?: (snapshot: CitationSnapshot) => boolean

  // Audit trail persistence
  lastCompletedAuditId?: string | null

  // Staleness detection
  isStale?: boolean
}

// ── Context ──────────────────────────────────────────────────────────────────

const ReviewPanelContext = createContext<ReviewPanelContextValue | null>(null)

// ── Provider ─────────────────────────────────────────────────────────────────

export function ReviewPanelProvider({
  children,
  apiAuditResult,
  sourceLookup,
  isAuditing,
  hasAuditResults,
  progressMessage,
  auditError,
  activeDocumentId,
  selectedClaimId,
  setSelectedClaimId,
  focusedClaimIndices,
  setFocusedClaimIndices,
  activeSourceId,
  setActiveSourceId,
  activeBreakdownIndices,
  onRunAudit,
  onRunIncrementalAudit,
  onHighlightClause,
  onSourceReject,
  onReprocessMetadata,
  onUpdateCitation,
  onImportToLibrary,
  applyEdit,
  viewMode,
  setViewMode,
  surgeryClaimId,
  setSurgeryClaimId,
  abbreviations,
  onAbbreviationsChange,
  draftId,
  onClaimStatusChange,
  onHiddenSourcesChange,
  onRemoveCitationFromDoc,
  onRestoreCitationToDoc,
  lastCompletedAuditId,
  isStale: isStaleFromProps = false,
}: ReviewPanelProviderProps) {
  // Phase 2b state — owned by provider (except viewMode/surgeryClaimId, lifted to App.tsx)
  const [hiddenSources, setHiddenSources] = useState<Map<string, Set<string>>>(new Map())
  const [hiddenPassages, setHiddenPassages] = useState<Map<string, Set<string>>>(new Map())

  const hideSource = useCallback((claimId: string, sourceId: string) => {
    setHiddenSources(prev => {
      const next = new Map(prev)
      const set = new Set(next.get(claimId) || [])
      set.add(sourceId)
      next.set(claimId, set)
      return next
    })
  }, [])

  const unhideSource = useCallback((claimId: string, sourceId: string) => {
    setHiddenSources(prev => {
      const next = new Map(prev)
      const set = new Set(next.get(claimId) || [])
      set.delete(sourceId)
      if (set.size === 0) next.delete(claimId)
      else next.set(claimId, set)
      return next
    })
  }, [])

  // Claim snapshots — declared here for persistence and decoration sync
  const [claimSnapshots, setClaimSnapshots] = useState<Map<string, ClaimSnapshot>>(new Map())

  // Audit trail — session-scoped, per claim
  const [auditLog, setAuditLog] = useState<Map<string, AuditEntry[]>>(new Map())
  // Tracks how many entries per claim have been flushed to backend audit_trail
  // (so we don't re-send rehydrated rows on the next flush). Declared early so
  // the rehydration effect below can seed it.
  const persistedCountsRef = useRef<Map<string, number>>(new Map())

  const addAuditEntry = useCallback((claimId: string, action: string, type: 'user' | 'auto') => {
    setAuditLog(prev => {
      const next = new Map(prev)
      const entries = [...(next.get(claimId) || [])]
      entries.push({
        timestamp: new Date().toISOString(),
        action,
        type,
      })
      next.set(claimId, entries)
      return next
    })
  }, [])

  // D13 Audit Trail v1.5: rehydrate auditLog from backend on audit result
  // arrival. Backend writes the seed entries ("Initial analysis completed",
  // "N sources matched, M contradictions") at audit-finalization with the
  // audit's real created_at timestamp — no more fake client-side timestamps.
  // Keyed by lastCompletedAuditId (real audit UUID), NOT apiAuditResult.query
  // which is the user's query text.
  const seededRef = useRef<string | null>(null)
  useEffect(() => {
    if (!apiAuditResult || !lastCompletedAuditId) return
    if (seededRef.current === lastCompletedAuditId) return
    seededRef.current = lastCompletedAuditId
    const auditId = lastCompletedAuditId

    let cancelled = false
    ;(async () => {
      try {
        const resp = await apiFetch(`/api/audits/${auditId}/trail`)
        if (!resp.ok) return
        const data = await resp.json() as { entries: Array<{ claim_uuid?: string | null; claim_index?: string | null; description?: string; entry_type?: string; occurred_at?: string }> }
        if (cancelled) return

        // Build claim_uuid → panel claimId (string index) lookup from the
        // current atomic_breakdown so backend rows can be grouped into the
        // panel's claimId-keyed Map<claimId, AuditEntry[]>.
        const uuidToPanelId = new Map<string, string>()
        apiAuditResult.atomic_breakdown.forEach((entry, i) => {
          if (entry.claim_uuid) uuidToPanelId.set(entry.claim_uuid, String(i))
        })

        const grouped = new Map<string, AuditEntry[]>()
        for (const e of data.entries ?? []) {
          const panelId = (e.claim_uuid && uuidToPanelId.get(e.claim_uuid)) || e.claim_index || null
          if (!panelId) continue
          const list = grouped.get(panelId) ?? []
          const ts = e.occurred_at
            ? new Date(e.occurred_at).toISOString()
            : new Date().toISOString()
          list.push({
            timestamp: ts,
            action: e.description ?? '',
            type: (e.entry_type === 'user' ? 'user' : 'auto'),
          })
          grouped.set(panelId, list)
        }
        // Replace, don't merge: prev may carry stale entries from a previous
        // audit (the audit-change effect resets persistedCountsRef but used
        // to leave auditLog in place, leaking stale rows into the next
        // audit's flush). auditLog is now reset on audit transitions, so
        // prev should be empty here; if any in-flight addAuditEntry call
        // raced the fetch, those entries belonged to the prior audit and
        // must not carry into this one's audit_trail.
        setAuditLog(grouped)
        // Mark every rehydrated entry as already persisted, so flushAuditTrail
        // doesn't re-send seed rows on the next flush.
        const counts = new Map<string, number>()
        for (const [k, v] of grouped) counts.set(k, v.length)
        persistedCountsRef.current = counts
      } catch {
        // best-effort — empty trail is the fallback
      }
    })()
    return () => { cancelled = true }
  }, [apiAuditResult, lastCompletedAuditId])

  // ── Claim snapshots — source-removal badge recalculation ──────────────────

  // Seed snapshots when audit results arrive (same guard as auditLog).
  // sourceLookup is read via a ref so this seeding only re-runs on audit changes,
  // not whenever the library mutates — a re-seed would clobber user state.
  const sourceLookupRef = useRef(sourceLookup)
  useEffect(() => { sourceLookupRef.current = sourceLookup })
  const snapshotSeededRef = useRef<string | null>(null)
  useEffect(() => {
    if (!apiAuditResult || !lastCompletedAuditId) return
    if (snapshotSeededRef.current === lastCompletedAuditId) return
    snapshotSeededRef.current = lastCompletedAuditId

    const snapshots = new Map<string, ClaimSnapshot>()
    const restoredHidden = new Map<string, Set<string>>()
    const restoredHiddenPassages = new Map<string, Set<string>>()

    apiAuditResult.atomic_breakdown.forEach((entry, i) => {
      const claimId = String(i)
      const evidences = (entry.evidences ?? []).filter(ev => !!ev?.source_doc)
      const sources: ClaimSourceEntry[] = evidences.map(ev => ({
        source_doc: ev.source_doc,
        support_level: ev.support_level,
        reasoning: ev.reasoning,
      }))

      const snapshot: ClaimSnapshot = {
        claimId,
        claimUuid: entry.claim_uuid,
        originalStatus: entry.status,
        effectiveStatus: entry.status,
        originalAnalysis: entry.reasoning || '',
        effectiveAnalysis: entry.reasoning || '',
        suggestedFix: entry.suggested_edit ?? null,
        suggestedFixCleared: false,
        sources,
      }

      // Rehydrate from persisted review_state
      const rs = entry.review_state
      if (rs?.hidden_source_ids?.length) {
        const hiddenSet = new Set(rs.hidden_source_ids)
        restoredHidden.set(claimId, hiddenSet)
        const activeSources = sources.filter(s => !hiddenSet.has(s.source_doc))
        const hiddenSourcesList = sources.filter(s => hiddenSet.has(s.source_doc))
        const newStatus = recalculateStatus(activeSources)
        snapshot.effectiveStatus = newStatus
        snapshot.effectiveAnalysis = generateAnalysisUpdate(
          activeSources, hiddenSourcesList, newStatus as VerificationStatus,
          (doc: string) => citationLabel(sourceLookupRef.current.get(doc), doc),
        )
        snapshot.suggestedFixCleared = newStatus !== snapshot.originalStatus && !!snapshot.suggestedFix
      }
      // Rehydrate per-passage removals. Status is unaffected by passage-level
      // curation — the source still supports the claim, the writer is just
      // pruning which passages are surfaced to MLR.
      if (rs?.hidden_passage_ids?.length) {
        restoredHiddenPassages.set(claimId, new Set(rs.hidden_passage_ids))
      }
      snapshots.set(claimId, snapshot)
    })

    setClaimSnapshots(snapshots)
    setHiddenSources(restoredHidden)
    setHiddenPassages(restoredHiddenPassages)
  }, [apiAuditResult, lastCompletedAuditId])

  // Helper: get source label for analysis text generation
  const getSourceLabel = useCallback((sourceDoc: string): string => {
    return citationLabel(sourceLookup.get(sourceDoc), sourceDoc)
  }, [sourceLookup])

  const removeSourceFromClaim = useCallback((claimId: string, sourceId: string) => {
    const snapshot = claimSnapshots.get(claimId)
    if (!snapshot) return

    // 1. Update hiddenSources
    hideSource(claimId, sourceId)

    // 2. Compute new hidden set (current + new)
    const currentHidden = hiddenSources.get(claimId) ?? new Set<string>()
    const newHiddenSet = new Set(currentHidden)
    newHiddenSet.add(sourceId)

    // 3. Filter to active sources
    const activeSources = snapshot.sources.filter(s => !newHiddenSet.has(s.source_doc))
    const hiddenSourcesList = snapshot.sources.filter(s => newHiddenSet.has(s.source_doc))

    // 4. Recalculate status
    const newStatus = recalculateStatus(activeSources)
    const oldStatus = snapshot.effectiveStatus

    // 5. Generate analysis text
    const newAnalysis = generateAnalysisUpdate(activeSources, hiddenSourcesList, newStatus, getSourceLabel)

    // 6. Suggested fix logic: clear if status → verified and fix exists
    const suggestedFixCleared = newStatus !== snapshot.originalStatus && !!snapshot.suggestedFix

    // 7. Update snapshot
    setClaimSnapshots(prev => {
      const next = new Map(prev)
      next.set(claimId, {
        ...snapshot,
        effectiveStatus: newStatus,
        effectiveAnalysis: newAnalysis,
        suggestedFixCleared,
      })
      return next
    })

    // 8. Audit entries
    addAuditEntry(claimId, `Source removed: ${getSourceLabel(sourceId)}`, 'user')
    if (newStatus !== oldStatus) {
      const oldLabel = statusBadgeConfig[oldStatus]?.label ?? oldStatus
      const newLabel = statusBadgeConfig[newStatus]?.label ?? newStatus
      addAuditEntry(claimId, `Badge updated: ${oldLabel} → ${newLabel}`, 'auto')
    }
    if (suggestedFixCleared && !snapshot.suggestedFixCleared) {
      addAuditEntry(claimId, 'Suggested fix removed (no longer needed)', 'auto')
    }

    // 9. Persist to backend (carry along any per-passage removals — the
    //    review_state column holds both maps).
    if (snapshot.claimUuid) {
      const hiddenPassageIds = [...(hiddenPassages.get(claimId) ?? new Set<string>())]
      persistReviewState(snapshot.claimUuid, [...newHiddenSet], hiddenPassageIds)
    }
  }, [claimSnapshots, hiddenSources, hiddenPassages, hideSource, addAuditEntry, getSourceLabel])

  const restoreSourceToClaim = useCallback((claimId: string, sourceId: string) => {
    const snapshot = claimSnapshots.get(claimId)
    if (!snapshot) return

    // 1. Update hiddenSources
    unhideSource(claimId, sourceId)

    // 2. Compute new hidden set (current minus restored)
    const currentHidden = hiddenSources.get(claimId) ?? new Set<string>()
    const newHiddenSet = new Set(currentHidden)
    newHiddenSet.delete(sourceId)

    const oldStatus = snapshot.effectiveStatus
    let newStatus: string
    let newAnalysis: string

    if (newHiddenSet.size === 0) {
      // All sources restored — bypass recalculateStatus to preserve verified_no_coords
      newStatus = snapshot.originalStatus
      newAnalysis = snapshot.originalAnalysis
    } else {
      const activeSources = snapshot.sources.filter(s => !newHiddenSet.has(s.source_doc))
      const hiddenSourcesList = snapshot.sources.filter(s => newHiddenSet.has(s.source_doc))
      newStatus = recalculateStatus(activeSources)
      newAnalysis = generateAnalysisUpdate(activeSources, hiddenSourcesList, newStatus as VerificationStatus, getSourceLabel)
    }

    // Suggested fix: clear when status diverges from original, restore on full restore
    const suggestedFixCleared = newStatus !== snapshot.originalStatus && !!snapshot.suggestedFix

    setClaimSnapshots(prev => {
      const next = new Map(prev)
      next.set(claimId, {
        ...snapshot,
        effectiveStatus: newStatus,
        effectiveAnalysis: newAnalysis,
        suggestedFixCleared,
      })
      return next
    })

    // Audit entries
    addAuditEntry(claimId, `Source restored: ${getSourceLabel(sourceId)}`, 'user')
    if (newStatus !== oldStatus) {
      const oldLabel = statusBadgeConfig[oldStatus]?.label ?? oldStatus
      const newLabel = statusBadgeConfig[newStatus]?.label ?? newStatus
      addAuditEntry(claimId, `Badge updated: ${oldLabel} → ${newLabel}`, 'auto')
    }
    if (snapshot.suggestedFixCleared && !suggestedFixCleared) {
      addAuditEntry(claimId, 'Suggested fix restored', 'auto')
    }

    // Persist to backend (carry along any per-passage removals).
    if (snapshot.claimUuid) {
      const hiddenPassageIds = [...(hiddenPassages.get(claimId) ?? new Set<string>())]
      persistReviewState(snapshot.claimUuid, [...newHiddenSet], hiddenPassageIds)
    }
  }, [claimSnapshots, hiddenSources, hiddenPassages, unhideSource, addAuditEntry, getSourceLabel])

  // Per-passage soft-removal. Status taxonomy is unaffected — source still
  // supports the claim. If this removes the last visible passage from a
  // source, cascade to source-level removal so the source row disappears
  // entirely (no phantom citation with zero evidence).
  const removePassageFromClaim = useCallback((claimId: string, sourceId: string, passageId: string) => {
    const snapshot = claimSnapshots.get(claimId)
    if (!snapshot?.claimUuid) return
    if (!apiAuditResult) return

    const claimIdx = Number(claimId)
    const breakdown = apiAuditResult.atomic_breakdown[claimIdx]
    const evidence = breakdown?.evidences?.find(ev => ev.source_doc === sourceId)
    if (!evidence) return

    const currentHiddenForClaim = hiddenPassages.get(claimId) ?? new Set<string>()
    const newHiddenSet = new Set(currentHiddenForClaim)
    newHiddenSet.add(passageId)

    // Cascade: if every passage from this source is now hidden, remove the
    // source itself rather than leaving a passage-less card.
    const allFromSource = evidence.proof_passages.map(p => p.passage_id).filter(Boolean)
    const remainingForSource = allFromSource.filter(pid => !newHiddenSet.has(pid))
    const sourceLabel = getSourceLabel(sourceId)

    if (allFromSource.length > 0 && remainingForSource.length === 0) {
      // Drop the about-to-be-cascaded passage IDs from hiddenPassages — the
      // source-level hide subsumes them — then delegate to source removal.
      // This keeps review_state tidy and avoids redundant entries.
      setHiddenPassages(prev => {
        const next = new Map(prev)
        const set = new Set(next.get(claimId) || [])
        for (const pid of allFromSource) set.delete(pid)
        if (set.size === 0) next.delete(claimId)
        else next.set(claimId, set)
        return next
      })
      addAuditEntry(claimId, `Passage removed (last from source — ${sourceLabel} dropped)`, 'user')
      removeSourceFromClaim(claimId, sourceId)
      return
    }

    setHiddenPassages(prev => {
      const next = new Map(prev)
      next.set(claimId, newHiddenSet)
      return next
    })

    addAuditEntry(claimId, `Passage removed (${sourceLabel})`, 'user')

    const hiddenSourceIds = [...(hiddenSources.get(claimId) ?? new Set<string>())]
    persistReviewState(snapshot.claimUuid, hiddenSourceIds, [...newHiddenSet])
  }, [apiAuditResult, claimSnapshots, hiddenPassages, hiddenSources, addAuditEntry, getSourceLabel, removeSourceFromClaim])

  const restorePassageToClaim = useCallback((claimId: string, sourceId: string, passageId: string) => {
    const snapshot = claimSnapshots.get(claimId)
    if (!snapshot?.claimUuid) return

    const currentHiddenForClaim = hiddenPassages.get(claimId) ?? new Set<string>()
    if (!currentHiddenForClaim.has(passageId)) return
    const newHiddenSet = new Set(currentHiddenForClaim)
    newHiddenSet.delete(passageId)

    setHiddenPassages(prev => {
      const next = new Map(prev)
      if (newHiddenSet.size === 0) next.delete(claimId)
      else next.set(claimId, newHiddenSet)
      return next
    })

    addAuditEntry(claimId, `Passage restored (${getSourceLabel(sourceId)})`, 'user')

    const hiddenSourceIds = [...(hiddenSources.get(claimId) ?? new Set<string>())]
    persistReviewState(snapshot.claimUuid, hiddenSourceIds, [...newHiddenSet])
  }, [claimSnapshots, hiddenPassages, hiddenSources, addAuditEntry, getSourceLabel])

  const onRemoveCitationFromDocRef = useRef(onRemoveCitationFromDoc)
  const onRestoreCitationToDocRef = useRef(onRestoreCitationToDoc)
  useEffect(() => {
    onRemoveCitationFromDocRef.current = onRemoveCitationFromDoc
    onRestoreCitationToDocRef.current = onRestoreCitationToDoc
  })

  // D11: Session-scoped record of citations the writer has removed via D4.
  // Cleared on reload (not persisted to review_state). The doc is the truth;
  // this is in-session "oops" recovery only.
  const [removedCitations, setRemovedCitations] = useState<Map<string, CitationSnapshot>>(new Map())

  const removeCitation = useCallback((claimId: string, triggerSourceId: string): boolean => {
    const snapshot = claimSnapshots.get(claimId)
    if (!snapshot?.claimUuid) return false

    // Doc-mutation: structurally remove the <sup> citation node from prose.
    // App.tsx implements via Workbench helpers, which set the auditProtection
    // bypass meta. Transaction is left in undo history so ⌘Z restores it.
    const captured = onRemoveCitationFromDocRef.current?.(snapshot.claimUuid)
    if (!captured) return false

    setRemovedCitations(prev => {
      const next = new Map(prev)
      next.set(snapshot.claimUuid!, captured)
      return next
    })

    addAuditEntry(claimId, `Citation removed (last source: ${getSourceLabel(triggerSourceId)})`, 'user')
    return true
  }, [claimSnapshots, addAuditEntry, getSourceLabel])

  const restoreCitation = useCallback((claimUuid: string): boolean => {
    const snap = removedCitations.get(claimUuid)
    if (!snap) return false

    const ok = onRestoreCitationToDocRef.current?.(snap) ?? false
    if (!ok) return false

    setRemovedCitations(prev => {
      const next = new Map(prev)
      next.delete(claimUuid)
      return next
    })

    // Find the panel claimId (string index) for this claim_uuid to log against.
    let panelClaimId: string | null = null
    for (const [id, s] of claimSnapshots) {
      if (s.claimUuid === claimUuid) { panelClaimId = id; break }
    }
    if (panelClaimId) {
      addAuditEntry(panelClaimId, 'Citation restored', 'user')
    }
    return true
  }, [removedCitations, claimSnapshots, addAuditEntry])

  // ── Decoration sync — lift effective statuses to App.tsx for Workbench ─────
  const onClaimStatusChangeRef = useRef(onClaimStatusChange)
  useEffect(() => { onClaimStatusChangeRef.current = onClaimStatusChange })

  useEffect(() => {
    if (claimSnapshots.size === 0) return
    const map = new Map<string, string>()
    for (const [claimId, snap] of claimSnapshots) {
      map.set(claimId, snap.effectiveStatus)
    }
    onClaimStatusChangeRef.current?.(map)
  }, [claimSnapshots])

  // ── Source-registry sync — lift hidden sources (keyed by claim_uuid) to
  //    App.tsx → Workbench → sourceRegistryPlugin (meta dispatch).
  //    Internal state is keyed by claimId (string index); the plugin needs
  //    the claim_uuid bridge that matches citation node `claimId` attr. ─────
  const onHiddenSourcesChangeRef = useRef(onHiddenSourcesChange)
  useEffect(() => { onHiddenSourcesChangeRef.current = onHiddenSourcesChange })

  useEffect(() => {
    if (claimSnapshots.size === 0) return
    const byUuid = new Map<string, Set<string>>()
    for (const [claimId, hiddenSet] of hiddenSources) {
      if (hiddenSet.size === 0) continue
      const snap = claimSnapshots.get(claimId)
      if (snap?.claimUuid) byUuid.set(snap.claimUuid, new Set(hiddenSet))
    }
    onHiddenSourcesChangeRef.current?.(byUuid)
  }, [hiddenSources, claimSnapshots])

  // ── Audit trail persistence — batch-flush to backend ───────────────────────
  const auditLogRef = useRef(auditLog)
  const lastCompletedAuditIdRef = useRef(lastCompletedAuditId)
  useEffect(() => {
    auditLogRef.current = auditLog
    lastCompletedAuditIdRef.current = lastCompletedAuditId
  })

  // claim_uuid lookup ref — needed by flush helpers so the audit_trail rows
  // they write are keyed by stable claim_uuid (D13).
  const claimUuidLookupRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    const m = new Map<string, string>()
    for (const [claimId, snap] of claimSnapshots) {
      if (snap.claimUuid) m.set(claimId, snap.claimUuid)
    }
    claimUuidLookupRef.current = m
  }, [claimSnapshots])

  // Flush on unmount + beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      const id = lastCompletedAuditIdRef.current
      if (!id) return
      beaconFlushAuditTrail(id, auditLogRef.current, persistedCountsRef.current, claimUuidLookupRef.current)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Flush on unmount (provider removed when panel closes or draft switches)
      const id = lastCompletedAuditIdRef.current
      if (!id) return
      flushAuditTrail(id, auditLogRef.current, persistedCountsRef.current, claimUuidLookupRef.current)
        .then(counts => { persistedCountsRef.current = counts })
        .catch(() => {}) // best-effort
    }
  }, [])

  // Flush when audit changes (new audit replaces old)
  const prevAuditIdRef = useRef<string | null>(lastCompletedAuditId ?? null)
  useEffect(() => {
    if (lastCompletedAuditId === prevAuditIdRef.current) return
    const oldId = prevAuditIdRef.current
    prevAuditIdRef.current = lastCompletedAuditId ?? null

    if (oldId && auditLogRef.current.size > 0) {
      flushAuditTrail(oldId, auditLogRef.current, persistedCountsRef.current, claimUuidLookupRef.current)
        .then(counts => { persistedCountsRef.current = counts })
        .catch(() => {})
    }
    // Reset both auditLog and persisted counts for the new audit. Resetting
    // only the counts (the previous behavior) leaked the prior audit's
    // entries into the new audit's auditLog, where the rehydration's
    // merge would stack them and a later flush would post them under the
    // new audit_id. Pharma compliance: every audit's trail must be its own.
    setAuditLog(new Map())
    persistedCountsRef.current = new Map()
  }, [lastCompletedAuditId])

  const value: ReviewPanelContextValue = {
    apiAuditResult,
    sourceLookup,
    isAuditing,
    hasAuditResults,
    progressMessage,
    auditError,
    activeDocumentId,
    isStale: isStaleFromProps,
    selectedClaimId,
    setSelectedClaimId,
    focusedClaimIndices,
    setFocusedClaimIndices,
    activeSourceId,
    setActiveSourceId,
    activeBreakdownIndices,
    onRunAudit,
    onRunIncrementalAudit,
    onHighlightClause,
    onSourceReject,
    onReprocessMetadata,
    onUpdateCitation,
    onImportToLibrary,
    applyEdit,
    viewMode,
    setViewMode,
    surgeryClaimId,
    setSurgeryClaimId,
    hiddenSources,
    hideSource,
    unhideSource,
    hiddenPassages,
    removePassageFromClaim,
    restorePassageToClaim,
    abbreviations,
    onAbbreviationsChange,
    draftId,
    claimSnapshots,
    removeSourceFromClaim,
    restoreSourceToClaim,
    removeCitation,
    removedCitations,
    restoreCitation,
    auditLog,
    addAuditEntry,
  }

  return (
    <ReviewPanelContext.Provider value={value}>
      {children}
    </ReviewPanelContext.Provider>
  )
}

// ── Hook ─────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components -- context hook colocated with provider
export function useReviewPanel() {
  const ctx = useContext(ReviewPanelContext)
  if (!ctx) throw new Error('useReviewPanel must be used inside ReviewPanelProvider')
  return ctx
}
